import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { ICompany } from '@nxsys/types/models/company';
import { useCallback, useMemo, useRef, useState } from 'react';
import EmployerSettingsForm, {
  EmployerSettingsFormValues,
} from './components/EmployerSettingsForm';
import RTISettingsForm, {
  RTISettingsFormValues,
} from './components/RTISettingsForm';

import AppButton from '@nxsys/components/AppButton';
import { Icons } from '@nxsys/types/icons';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import RateBands from './components/RateBands';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
};

enum HMRCTabEnum {
  EMPLOYER_SETTINGS = 'EMPLOYER_SETTINGS',
  RTI_SETTINGS = 'RTI_SETTINGS',
  HMRC_RATES_BANDS = 'HMRC_RATES_BANDS',
}

const HMRCTab: React.FC<Props> = ({ data, updateData }) => {
  const { messages } = useTypedIntl();
  const [isUpdating, setIsUpdating] = useState(false);

  const employerSettingsFormRef = useRef<{ submit: () => void }>(null);
  const rtiSettingsFormRef = useRef<{ submit: () => void }>(null);

  const tabs = useMemo(
    () => [
      {
        tab: messages['common.employerSettings'],
        key: HMRCTabEnum.EMPLOYER_SETTINGS,
      },
      {
        tab: messages['common.rtiSettings'],
        key: HMRCTabEnum.RTI_SETTINGS,
      },
      {
        tab: messages['common.hmrcRatesBands'],
        key: HMRCTabEnum.HMRC_RATES_BANDS,
      },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState<HMRCTabEnum>(
    HMRCTabEnum.EMPLOYER_SETTINGS,
  );

  const handleTabChange = (key: HMRCTabEnum) => {
    setActiveTab(key);
  };

  const handleEmployerSettingsSubmit = useCallback(
    async (values: EmployerSettingsFormValues) => {
      setIsUpdating(true);
      try {
        const toNum = (v: unknown) => {
          const n = Number(v);
          return isNaN(n) ? 0 : n;
        };
        const params: UpdateCompanyByIdParams = {
          empSetting: {
            ...(data?.empSetting as any),
            taxDistrict: values.taxDistrict || '',
            accountsOfficeReference: values.accountsOfficeReference || '',
            employeeReference: values.employeeReference || '',
            claimEmpAllowance: values.claimEmpAllowance,
            allowanceClaimedSoFar: toNum(values.allowanceClaimedSoFar),
            apprenticeLevyDue: values.apprenticeLevyDue,
            eligibleForSmallEmpRelief: values.eligibleForSmallEmpRelief,
            recoverableAmountsYTD: values.recoverableAmountsYTD,
            smpRecoveredYTD: toNum(values.smpRecoveredYTD),
            sppRecoveredYTD: toNum(values.sppRecoveredYTD),
            sapRecoveredYTD: toNum(values.sapRecoveredYTD),
            shppRecoveredYTD: toNum(values.shppRecoveredYTD),
            spbpRecoveredYTD: toNum(values.spbpRecoveredYTD),
            sncpRecoveredYTD: toNum(values.sncpRecoveredYTD),
            nicCompensationOnSmpYTD: toNum(values.nicCompensationOnSmpYTD),
            nicCompensationOnSppYTD: toNum(values.nicCompensationOnSppYTD),
            nicCompensationOnSapYTD: toNum(values.nicCompensationOnSapYTD),
            nicCompensationOnShppYTD: toNum(values.nicCompensationOnShppYTD),
            nicCompensationOnSpbpYTD: toNum(values.nicCompensationOnSpbpYTD),
            nicCompensationOnSncpYTD: toNum(values.nicCompensationOnSncpYTD),
            cisDeductionsSufferedYTD: toNum(values.cisDeductionsSufferedYTD),
            appLevyClaimedYTD: toNum(values.appLevyClaimedYTD),
          },
        };
        await updateData(params);
      } finally {
        setIsUpdating(false);
      }
    },
    [data, updateData],
  );

  const handleRTISettingsSubmit = useCallback(
    async (values: RTISettingsFormValues) => {
      setIsUpdating(true);
      try {
        const agentAddress: string[] = [];
        if (values.agentAddress1) agentAddress.push(values.agentAddress1);
        if (values.agentAddress2) agentAddress.push(values.agentAddress2);
        const params: UpdateCompanyByIdParams = {
          rtiSetting: {
            ...(data?.rtiSetting as any),
            enableRTI: values.enableRTI,
            userId: values.userId || null,
            password: values.password || data?.rtiSetting?.password || null,
            agentForename: values.agentForename || null,
            agentSurname: values.agentSurname || null,
            agentAddress: agentAddress,
            agentCity: values.agentCity || null,
            agentCounty: values.agentCounty || null,
            agentPostCode: values.agentPostCode || null,
            agentCountry: values.agentCountry?.value || null,
          },
        };
        await updateData(params);
      } finally {
        setIsUpdating(false);
      }
    },
    [data, updateData],
  );

  const handleSave = useCallback(() => {
    if (activeTab === HMRCTabEnum.EMPLOYER_SETTINGS) {
      employerSettingsFormRef.current?.submit();
    } else if (activeTab === HMRCTabEnum.RTI_SETTINGS) {
      rtiSettingsFormRef.current?.submit();
    }
  }, [activeTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case HMRCTabEnum.EMPLOYER_SETTINGS:
        return (
          <EmployerSettingsForm
            companyData={data}
            formRef={employerSettingsFormRef}
            onSubmit={handleEmployerSettingsSubmit}
          />
        );
      case HMRCTabEnum.RTI_SETTINGS:
        return (
          <RTISettingsForm
            companyData={data}
            formRef={rtiSettingsFormRef}
            onSubmit={handleRTISettingsSubmit}
          />
        );
      case HMRCTabEnum.HMRC_RATES_BANDS:
        return <RateBands />;
      default:
        return <></>;
    }
  };

  return (
    <AppCardTabListContainer
      tabList={tabs}
      activeTab={activeTab}
      handleTabChange={handleTabChange}
      className="!border-none"
    >
      <div className="pt-[16px]">{renderTabContent()}</div>
      {activeTab !== HMRCTabEnum.HMRC_RATES_BANDS && (
        <div className="flex items-center justify-start gap-3">
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle color={defaultTheme.theme.palette.white} />
            }
            onClick={handleSave}
            loading={isUpdating}
          />
        </div>
      )}
    </AppCardTabListContainer>
  );
};

export default HMRCTab;
